/**
 * Validation for every client → server payload. The server parses with these before
 * any game logic runs; anything that doesn't match is rejected outright.
 */
import { z } from 'zod';
import { t } from './text.js';
import { AVATAR_DEFAULTS, AVATAR_MAX_BYTES, BOT_LEVELS, CARD_SELECTIONS, CHAT_MAX_LENGTH, DECK_COLORS, DECKS, DICE_COUNT, DIE_FACES, DISPLAY_NAME_MAX_LENGTH, EMOTES, FEEDBACK_CATEGORIES, FEEDBACK_IMAGE_MAX_CHARS, PLAY_STYLES, TOSS_MAX_SPEED, TOSS_MAX_SPIN, FEEDBACK_MAX_IMAGES, FEEDBACK_MAX_LENGTH, FEEDBACK_SNAPSHOT_MAX_CHARS, IDLE_LIMITS, MAX_PLAYERS, VOICE_SDP_MAX_CHARS, } from './constants.js';
const id = z.number().int().positive().max(2_147_483_647);
/** Player ids: humans are positive, bots negative. */
const playerId = z
    .number()
    .int()
    .min(-2_147_483_647)
    .max(2_147_483_647)
    .refine((n) => n !== 0);
const dieIndex = z
    .number()
    .int()
    .min(0)
    .max(DICE_COUNT - 1);
const keep = z.array(dieIndex).max(DICE_COUNT);
const mods = z
    .array(z.strictObject({ index: dieIndex, value: z.number().int().min(1).max(DIE_FACES) }))
    .max(DICE_COUNT);
/** Motion dice: how the dice were thrown (`DiceToss`). Only shapes the 3D dice on screen; never the roll. */
const axis = (max) => z.number().min(-max).max(max);
const toss = z
    .strictObject({
    v: z.tuple([axis(TOSS_MAX_SPEED), axis(TOSS_MAX_SPEED), axis(TOSS_MAX_SPEED)]),
    w: z.tuple([axis(TOSS_MAX_SPIN), axis(TOSS_MAX_SPIN), axis(TOSS_MAX_SPIN)]),
    s: z.number().int().min(0).max(0xffffffff),
})
    .optional();
/** Debug mode's 5x button: treat this roll as five of a kind (any die may be set to anything). */
const debug5x = z.boolean().optional();
export const payloadSchemas = {
    // The host's Play Style, the new lobby's starting Card Selection.
    'lobby:create': z.strictObject({ cardSelection: z.enum(CARD_SELECTIONS) }),
    'lobby:quickPlay': z.strictObject({ cardSelection: z.enum(CARD_SELECTIONS) }),
    'lobby:join': z.strictObject({ gameId: id }),
    'lobby:watch': z.strictObject({ gameId: id }),
    'lobby:watchOnly': z.strictObject({ on: z.boolean() }),
    'lobby:chooseColor': z.strictObject({ color: z.enum(DECK_COLORS) }),
    'lobby:addBot': z.strictObject({ level: z.enum(BOT_LEVELS) }),
    'lobby:settings': z.strictObject({
        cardSelection: z.enum(CARD_SELECTIONS),
        ghostRolls: z.boolean(),
        deck: z.enum(DECKS),
        idleLimit: z.literal(IDLE_LIMITS),
        // Optional: clients from before the option existed don't send it.
        botCollusion: z.boolean().default(false),
    }),
    'lobby:removeBot': z.strictObject({ playerId: playerId.refine((n) => n < 0) }),
    // `random`: picked at random by the player's Random play style (counted on the leaderboards).
    'game:chooseCard': z.strictObject({ cardId: id, random: z.boolean().optional() }),
    'game:tutorial': z.strictObject({ on: z.boolean() }),
    'game:roll': z.strictObject({ keep, toss }),
    // Optional payload: clients from before motion dice send none.
    'war:roll': z.strictObject({ toss }).optional(),
    'game:ghostRoll': z.strictObject({ toss }).optional(),
    'game:endTurn': z.strictObject({ keep, mods, debug5x }),
    // Power changes that make five of a kind: every die goes wild.
    'game:wild': z.strictObject({ mods }),
    'game:attack': z.strictObject({ targets: z.array(playerId).min(1).max(MAX_PLAYERS), keep, mods, debug5x }),
    // Allow Bot Collusion: offer `to` a deal, saying you're after `target`; answer the deal waiting on you.
    'game:offerDeal': z.strictObject({ to: playerId, target: playerId }),
    'game:answerDeal': z.strictObject({ answer: z.enum(['accept', 'decline', 'counter']) }),
    // `to`: a private message to that (online, human) player instead of the room.
    'chat:send': z.strictObject({
        text: z.string().trim().min(1).max(CHAT_MAX_LENGTH),
        to: playerId.refine((n) => n > 0).optional(),
    }),
    'client:visible': z.strictObject({ visible: z.boolean() }),
    'emote:send': z.strictObject({ emote: z.enum(EMOTES) }),
    // Voice chat: WebRTC setup relayed to one other player in your game's voice chat.
    'voice:signal': z.strictObject({
        to: playerId.refine((n) => n > 0),
        data: z.discriminatedUnion('type', [
            z.strictObject({ type: z.enum(['offer', 'answer']), sdp: z.string().max(VOICE_SDP_MAX_CHARS) }),
            z.strictObject({
                type: z.literal('ice'),
                candidate: z.strictObject({
                    candidate: z.string().max(1024),
                    sdpMid: z.string().max(64).nullish(),
                    sdpMLineIndex: z.number().int().min(0).max(64).nullish(),
                    usernameFragment: z.string().max(256).nullish(),
                }),
            }),
        ]),
    }),
};
/** Display names: printable characters only, trimmed, collapsed whitespace. */
export const displayNameSchema = z
    .string()
    .transform((s) => s.replace(/\s+/g, ' ').trim())
    .pipe(z
    .string()
    .min(1, { error: () => t('error.nameEmpty') })
    .max(DISPLAY_NAME_MAX_LENGTH, { error: () => t('error.nameTooLong', { max: DISPLAY_NAME_MAX_LENGTH }) })
    .regex(/^[^\p{C}<>]+$/u, { error: () => t('error.nameInvalid') }));
/** Tester feedback report. Images are base64 data URLs (PNG, JPEG or WebP); the snapshot is any JSON. */
export const feedbackSchema = z.strictObject({
    category: z.enum(FEEDBACK_CATEGORIES),
    body: z.string().trim().min(1).max(FEEDBACK_MAX_LENGTH),
    snapshot: z.record(z.string(), z.unknown()).refine((s) => JSON.stringify(s).length <= FEEDBACK_SNAPSHOT_MAX_CHARS, {
        error: () => t('error.snapshotTooLarge'),
    }),
    images: z
        .array(z
        .string()
        .max(FEEDBACK_IMAGE_MAX_CHARS, { error: () => t('error.imageTooLarge') })
        .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/, { error: () => t('error.imageUnsupported') }))
        .max(FEEDBACK_MAX_IMAGES),
});
/** Your avatar: a built-in default, or an uploaded square image as a data URL (checked again once decoded). */
export const avatarSchema = z.union([
    z.strictObject({ default: z.number().int().min(1).max(AVATAR_DEFAULTS) }),
    z.strictObject({
        image: z
            .string()
            .max(Math.ceil(AVATAR_MAX_BYTES / 3) * 4 + 32, { error: () => t('error.avatarTooBig') })
            .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/, { error: () => t('error.imageUnsupported') }),
    }),
]);
/** `GET /api/leaderboard` query (`LeaderboardFilter`): each option left out means any. */
export const leaderboardFilterSchema = z.object({
    deck: z.enum(DECKS).optional(),
    cardSelection: z.enum(CARD_SELECTIONS).optional(),
    playStyle: z.enum(PLAY_STYLES).optional(),
});
//# sourceMappingURL=schemas.js.map